import type { FC, ReactNode } from 'react'
import type { Plan, PlanId, Viewer } from '@/types'

import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { formatInr } from '@/lib/format'

import {
    ArrowRight,
    Check,
    Crown,
    Minus,
    ShieldCheck,
    Sparkles
} from 'lucide-react'

import {
    COMPLETE_SAVINGS,
    FEE_BREAKDOWN,
    PLANS,
    YEAR_WISE_TOTAL,
    getPlan
} from '@/constants/campus'

interface PricingProps {
    viewer: Viewer | null
    onChoosePlan: (planId: PlanId) => void
}

type Mode = 'year' | 'complete'

const ease = [0.22, 1, 0.36, 1] as const

const portalReady = (viewer: Viewer | null): boolean =>
    viewer?.kind === 'student' && ['Pending', 'Active'].includes(viewer.student.accessStatus)

const PlanCard: FC<{ plan: Plan, index: number, ctaLabel: string, onChoose: () => void }> = ({ plan, index, ctaLabel, onChoose }): ReactNode => {
    const body = (
        <div className={cn('flex h-full flex-col rounded-[calc(1.5rem-1px)] p-7', plan.popular ? 'bg-[linear-gradient(180deg,#141C2E,#0D1320)]' : 'bg-white/[0.03]')}>
            <div className='flex items-center justify-between gap-3'>
                <span className='rounded-full bg-white/[0.07] px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-300'>
                    {plan.label} · {plan.semesters}
                </span>

                {plan.popular && (
                    <span className='inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-300 to-orange-400 px-2.5 py-1 text-[10px] font-extrabold tracking-[0.12em] text-slate-900'>
                        <Crown className='size-3' />
                        MOST POPULAR
                    </span>
                )}
            </div>

            <h3 className='mt-5 text-xl font-semibold text-white'>
                {plan.name}
            </h3>

            <p className='mt-2 min-h-[68px] text-sm leading-relaxed text-slate-400'>
                {plan.subtitle}
            </p>

            <div className='mt-6 flex items-end gap-2'>
                <span className='text-5xl font-semibold tracking-tight text-white'>
                    {formatInr(plan.price)}
                </span>

                <span className='pb-1.5 text-sm text-slate-400'>
                    {plan.cadence}
                </span>
            </div>

            <p className={cn('mt-2 text-xs font-medium', plan.popular ? 'text-emerald-300' : 'text-slate-500')}>
                {plan.popular ? `Under ₹100 a year over 4 years · ${formatInr(YEAR_WISE_TOTAL)}+ if paid year by year` : 'Paid once for the academic year'}
            </p>

            <motion.button
                type='button'
                onClick={onChoose}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                className={cn(
                    'group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-center text-sm font-semibold leading-snug transition-colors',
                    plan.popular ? 'bg-white text-ink shadow-[0_16px_40px_-14px_rgba(255,255,255,0.45)] hover:bg-slate-100' : 'border border-white/15 text-white hover:bg-white/[0.08]'
                )}
            >
                {ctaLabel}

                <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
            </motion.button>

            <div className='mt-7 border-t border-white/10 pt-6'>
                <p className='text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500'>
                    What's included
                </p>

                <ul className='mt-4 space-y-3'>
                    {plan.features.map(feature => (
                        <li key={feature} className='flex gap-3 text-sm leading-relaxed text-slate-300'>
                            <span className={cn('mt-0.5 grid size-5 shrink-0 place-items-center rounded-full', plan.popular ? 'bg-emerald-400/15 text-emerald-300' : 'bg-white/[0.07] text-slate-200')}>
                                <Check className='size-3' strokeWidth={3} />
                            </span>

                            {feature}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )

    return (
        <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, delay: index * 0.08, ease }}
            whileHover={{ y: -4 }}
            className={cn(
                'relative rounded-3xl p-px',
                plan.popular
                    ? 'bg-gradient-to-b from-indigo-400/80 via-sky-400/30 to-emerald-400/70 shadow-[0_30px_80px_-30px_rgba(99,102,241,0.6)]'
                    : 'bg-white/10 transition-colors hover:bg-white/20'
            )}
        >
            {body}
        </motion.div>
    )
}

const CompleteView: FC<{ ctaLabel: string, onChoose: () => void }> = ({ ctaLabel, onChoose }): ReactNode => {
    const complete = getPlan('complete')
    const yearPlans = PLANS.filter(plan => plan.year !== null)

    return (
        <div className='grid gap-5 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]'>
            <PlanCard plan={complete} index={0} ctaLabel={ctaLabel} onChoose={onChoose} />

            <motion.div
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1, ease }}
                className='flex flex-col rounded-3xl border border-white/10 bg-white/[0.03] p-7'
            >
                <p className='text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500'>
                    Year-wise route vs Complete Pass
                </p>

                <ul className='mt-5 divide-y divide-white/10'>
                    {yearPlans.map(plan => (
                        <li key={plan.id} className='flex items-center justify-between gap-4 py-3.5'>
                            <span>
                                <span className='block text-sm font-semibold text-white'>{plan.fullName}</span>
                                <span className='block text-xs text-slate-500'>{plan.semesters}</span>
                            </span>

                            <span className='font-mono text-sm text-slate-300'>{formatInr(plan.price)}</span>
                        </li>
                    ))}

                    <li className='flex items-center justify-between gap-4 py-3.5'>
                        <span>
                            <span className='block text-sm font-semibold text-white'>Placement Command Center</span>
                            <span className='block text-xs text-slate-500'>Readiness score, top-10% shortlist, 1-click drives</span>
                        </span>

                        <span className='inline-flex items-center gap-1 text-xs font-semibold text-slate-500'>
                            <Minus className='size-3.5' />
                            Complete Pass only
                        </span>
                    </li>
                </ul>

                <div className='mt-auto space-y-3 pt-6'>
                    <div className='flex items-center justify-between text-sm'>
                        <span className='text-slate-400'>Paid year by year</span>
                        <span className='font-mono text-slate-400 line-through decoration-rose-400/70'>{formatInr(YEAR_WISE_TOTAL)}+</span>
                    </div>

                    <div className='flex items-center justify-between'>
                        <span className='font-semibold text-white'>Complete Pass, one time</span>
                        <span className='font-mono text-2xl font-bold text-white'>{formatInr(complete.price)}</span>
                    </div>

                    <div className='flex items-center gap-2 rounded-2xl bg-emerald-400/10 px-4 py-3 text-sm font-semibold text-emerald-300 ring-1 ring-emerald-400/20'>
                        <Sparkles className='size-4' />
                        You save {formatInr(COMPLETE_SAVINGS)} and unlock placements for all 4 years
                    </div>
                </div>
            </motion.div>
        </div>
    )
}

const FeeBreakdown: FC = (): ReactNode => {
    const [planId, setPlanId] = useState<PlanId>('complete')
    const [hovered, setHovered] = useState<number | null>(null)
    const plan = getPlan(planId)

    return (
        <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, ease }}
            className='mt-14 rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8'
        >
            <div className='flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between'>
                <div>
                    <p className='inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-300'>
                        <ShieldCheck className='size-3.5' />
                        Where every rupee goes
                    </p>

                    <h3 className='mt-2 text-2xl font-semibold tracking-tight text-white sm:text-3xl'>
                        100% Transparent Campus Fee Breakdown
                    </h3>

                    <p className='mt-2 max-w-xl text-sm leading-relaxed text-slate-400'>
                        Every pass is split the same way. Pick a pass to see what its fee pays for, down to the paisa.
                    </p>
                </div>

                <div role='radiogroup' aria-label='Show the breakdown for' className='flex shrink-0 gap-1 self-start rounded-full bg-white/[0.06] p-1 lg:self-auto'>
                    {PLANS.map(option => (
                        <button
                            key={option.id}
                            type='button'
                            role='radio'
                            aria-checked={option.id === planId}
                            onClick={() => setPlanId(option.id)}
                            className={cn('relative rounded-full px-3.5 py-2 text-xs font-semibold transition-colors sm:px-4', option.id === planId ? 'text-ink' : 'text-slate-300 hover:text-white')}
                        >
                            {option.id === planId && <motion.span layoutId='fee-plan' className='absolute inset-0 rounded-full bg-white' transition={{ type: 'spring', stiffness: 420, damping: 34 }} />}
                            <span className='relative'>{formatInr(option.price)}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className='mt-8 flex h-4 overflow-hidden rounded-full bg-white/[0.04]' role='img' aria-label={FEE_BREAKDOWN.map(item => `${item.share}% ${item.label}`).join(', ')}>
                {FEE_BREAKDOWN.map((item, index) => (
                    <motion.span
                        key={item.label}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${item.share}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, delay: 0.15 + index * 0.12, ease }}
                        onMouseEnter={() => setHovered(index)}
                        onMouseLeave={() => setHovered(null)}
                        className='h-full border-r-2 border-midnight-deep transition-opacity duration-300 last:border-r-0'
                        style={{ backgroundColor: item.color, opacity: hovered === null || hovered === index ? 1 : 0.3 }}
                    />
                ))}
            </div>

            <ul className='mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
                {FEE_BREAKDOWN.map((item, index) => (
                    <li
                        key={item.label}
                        onMouseEnter={() => setHovered(index)}
                        onMouseLeave={() => setHovered(null)}
                        className={cn('rounded-2xl border p-4 transition-colors duration-300', hovered === index ? 'border-white/25 bg-white/[0.06]' : 'border-white/10')}
                    >
                        <div className='flex items-center gap-2'>
                            <span className='size-2.5 rounded-full' style={{ backgroundColor: item.color }} />

                            <span className='text-2xl font-semibold text-white'>{item.share}%</span>

                            <AnimatePresence mode='popLayout' initial={false}>
                                <motion.span
                                    key={`${planId}-${item.label}`}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    className='ml-auto font-mono text-sm text-slate-300'
                                >
                                    {formatInr((plan.price * item.share) / 100, 2)}
                                </motion.span>
                            </AnimatePresence>
                        </div>

                        <p className='mt-2 text-sm leading-relaxed text-slate-400'>
                            {item.label}
                        </p>
                    </li>
                ))}
            </ul>
        </motion.div>
    )
}

export const Pricing: FC<PricingProps> = ({ viewer, onChoosePlan }): ReactNode => {
    const [mode, setMode] = useState<Mode>('year')
    const toPortal = portalReady(viewer)
    const label = (plan: Plan) => toPortal ? "Open your portal" : plan.cta

    return (
        <section id='pricing' aria-labelledby='pricing-title' className='scroll-mt-20 bg-white px-3 py-8 sm:px-6'>
            <div className='relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] border border-white/10 bg-midnight-deep px-4 py-20 text-white shadow-2xl sm:px-8 sm:py-24 lg:px-12'>
                <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                    <div className='absolute -top-40 left-1/2 h-80 w-[900px] -translate-x-1/2 rounded-full bg-indigo-600/20 blur-[110px]' />
                    <div className='absolute bottom-0 right-0 size-96 rounded-full bg-emerald-500/10 blur-[120px]' />
                </div>

                <div className='relative'>
                    <div className='mx-auto max-w-3xl text-center'>
                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className='inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-300'
                        >
                            <Sparkles className='size-3.5 text-amber-300' />
                            Year-wise pricing
                        </motion.p>

                        <motion.h2
                            id='pricing-title'
                            initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
                            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease }}
                            className='mt-5 text-balance font-serif text-5xl leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl'
                        >
                            Plans that grow with your <span className='italic text-indigo-200'>B.Tech journey</span>
                        </motion.h2>

                        <p className='mx-auto mt-5 max-w-xl text-base leading-relaxed text-slate-400'>
                            Pick the pass for your current year, or lock in all four years at once. Your portal unlocks as soon as the T&P Cell verifies your payment.
                        </p>
                    </div>

                    <div className='mt-10 flex justify-center'>
                        <div role='tablist' aria-label='Pricing view' className='inline-flex flex-col gap-1 rounded-2xl border border-white/10 bg-white/[0.04] p-1.5 sm:flex-row sm:rounded-full'>
                            {([
                                ['year', "Year-Wise Annual Subscription", "1st · 2nd · 3rd · 4th Year"],
                                ['complete', "Complete 4-Year B.Tech Pass", "Save 45%"]
                            ] as const).map(([value, title, badge]) => (
                                <button
                                    key={value}
                                    type='button'
                                    role='tab'
                                    aria-selected={mode === value}
                                    onClick={() => setMode(value)}
                                    className={cn('relative flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors sm:rounded-full', mode === value ? 'text-ink' : 'text-slate-300 hover:text-white')}
                                >
                                    {mode === value && <motion.span layoutId='pricing-mode' className='absolute inset-0 rounded-xl bg-white sm:rounded-full' transition={{ type: 'spring', stiffness: 420, damping: 34 }} />}

                                    <span className='relative'>{title}</span>

                                    <span className={cn('relative rounded-full px-2 py-0.5 text-[10.5px] font-bold', value === 'complete' ? 'bg-emerald-500 text-white' : mode === value ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-slate-300')}>
                                        {badge}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className='mt-12'>
                        <AnimatePresence mode='wait' initial={false}>
                            <motion.div
                                key={mode}
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.35, ease }}
                            >
                                {mode === 'year' ? (
                                    <div className='grid gap-5 md:grid-cols-2 xl:grid-cols-4'>
                                        {PLANS.map((plan, index) => (
                                            <PlanCard key={plan.id} plan={plan} index={index} ctaLabel={label(plan)} onChoose={() => onChoosePlan(plan.id)} />
                                        ))}
                                    </div>
                                ) : (
                                    <CompleteView ctaLabel={label(getPlan('complete'))} onChoose={() => onChoosePlan('complete')} />
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <p className='mt-6 text-center text-xs text-slate-500'>
                        4th Year students choose the Complete Pass, which includes the full Placement Command Center.
                    </p>

                    <FeeBreakdown />
                </div>
            </div>
        </section>
    )
}
